import { has, reduce, unionBy, find, map, isEmpty, sortBy } from "lodash";
import React, { useCallback } from "react";
import styled from "@emotion/styled";

import useAllDownloadData from "~/hooks/data/useAllDownloadData";
import useGetBasePath from "~/hooks/useGetBasePath";

import UserContent from "~/components/UserContent";
import IconLink from "~/components/links/IconLink";
import GatedDocumentButton from "~/components/buttons/GatedDocumentButton";
import CADIcon from "~/components/CADIcon";
import FaIcon from "~/components/FaIcon";
import ProductFlag from "~/components/ProductFlag";

import { sanitizeAndParseHTML } from "~/utilities/helpers";
import { getUrlFromElasticsearch } from "~/utilities/searchHelpers";

import { formatArticleUrl } from "~/utilities/format";

const DocWrapper = styled.div``;

export const DocTitle = styled.span`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: ${({ displayContent }) => (displayContent ? `1.2` : `1`)};

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletPortrait}) {
        font-size: ${({ displayContent }) =>
            displayContent ? `1.8rem` : `2rem`};
    }
`;
export const DocTitleInnerWrapper = styled.div`
    display: inline-block;
`;

const Icon = styled(FaIcon)`
    margin-right: 10px;
`;

export const getIconByMimeTypeName = (post_mime_type) => {
    const mime_type = post_mime_type.replace(/^.+\//, "");
    switch (mime_type) {
        case "pdf":
            return "file-pdf";
        case "txt":
            return "file-alt";
        case "zip":
            return "file-zipper";
        case "ppsx":
            return "file-powerpoint";
        case "video":
            return "video";
        case "mp4":
            return "video";
        case "step":
            return "cubes";
        case "link":
            return "external-link-alt";
        default:
            return "file-alt";
    }
};

export const getIconName = (
    docAttributes,
    defaultIconName = "link",
    formats = [],
    iconClassOverride,
) => {
    let iconName = defaultIconName;
    const format = formats?.length ? formats[0] : {};

    if (iconClassOverride?.length > 1) {
        iconName = iconClassOverride;
    } else if (docAttributes?.iconClass) {
        iconName = docAttributes.iconClass;
    } else if (docAttributes?.isVideo) {
        iconName = "play";
    } else if (!isEmpty(format) && format.taxonomyDetails?.iconClass) {
        iconName = format.taxonomyDetails.iconClass;
    }

    if (typeof iconName !== "string") {
        iconName = "link";
    }

    return iconName;
};

const parseKCVersion = (rawVersion) => {
    let version = find(rawVersion, ({ value }) => value)?.value || "";

    if (version && !version.toLowerCase().startsWith("version")) {
        const intValue = parseInt(version);
        version = `Version: ${intValue ? intValue.toFixed(1) : version}`;
    }

    return version;
};

const getKcAsDoc = (elasticsearchDocument, getBasePath) => {
    const {
        post_title,
        post_id,
        meta,
        terms,
        latest_version_date_timestamp,
        icon_class_override,
    } = elasticsearchDocument;
    const { version: rawVersion } = meta || {};

    const url = getUrlFromElasticsearch(elasticsearchDocument, getBasePath);
    const version = parseKCVersion(rawVersion);
    const type = find(terms?.article_types, ({ name }) => name)?.name || "";
    const lastUpdated = formatDocumentVersionDate(
        latest_version_date_timestamp,
    );
    const iconName = icon_class_override || "link";

    return {
        version,
        lastUpdated,
        id: post_id,
        type: sanitizeAndParseHTML(type),
        name: (
            <IconLink color="purple" icon={iconName} to={url}>
                {sanitizeAndParseHTML(post_title)}
            </IconLink>
        ),
    };
};

const getKCUrlFromGraphQLDoc = (doc) => {
    const { slug, languageCode, translations } = doc;

    return formatArticleUrl("", slug, languageCode, translations);
};

export const createDocumentNameHtml = (
    doc,
    iconName,
    displayContent = true,
) => {
    return (
        <DocWrapper sortValue={doc.title} key={doc.id}>
            <GatedDocumentButton doc={doc} inline isCad={iconName === "cad"}>
                {iconName === "cad" ? (
                    <CADIcon withTitle />
                ) : (
                    <Icon name={iconName} />
                )}
                <DocTitle {...{ displayContent }}>
                    {sanitizeAndParseHTML(doc.title)}
                    {doc.documentsAttributes?.documentNewUntil ? (
                        <DocTitleInnerWrapper>
                            <ProductFlag
                                key={`fl-new-${doc.documentsAttributes.documentNewUntil}-0`}
                                {...{
                                    badgeName: "new",
                                    dateType: "until",
                                    date: doc.documentsAttributes
                                        .documentNewUntil,
                                }}
                            />
                        </DocTitleInnerWrapper>
                    ) : null}
                </DocTitle>
            </GatedDocumentButton>

            {doc.content && displayContent ? (
                <UserContent>
                    {sanitizeAndParseHTML(doc.content, {
                        replace: (domNode) => {
                            if (domNode.name === "p") {
                                domNode.name = "div";
                            }
                        },
                    })}
                </UserContent>
            ) : null}
        </DocWrapper>
    );
};

const checkIsArray = (value) => (Array.isArray(value) ? value : []);

const formatDocumentVersionDate = (latestVersionDate) => {
    if (!latestVersionDate) {
        return "";
    }

    const postModifiedDate = new Date(latestVersionDate);

    return new Intl.DateTimeFormat("en-US", {
        month: "2-digit",
        year: "numeric",
    }).format(postModifiedDate);
};

const sortDocuments = (docs, arrayToSortBy) => {
    if (arrayToSortBy?.length) {
        return docs.sort((a, b) => {
            let comparison = 0;

            const aIndex = arrayToSortBy.findIndex(
                (el) => el.slug === a.documentCategories[0]?.slug,
            );
            const bIndex = arrayToSortBy.findIndex(
                (el) => el.slug === b.documentCategories[0]?.slug,
            );

            const aNumber = aIndex !== -1 ? aIndex : a?.type?.charCodeAt();
            const bNumber = bIndex !== -1 ? bIndex : b?.type?.charCodeAt();

            if (aNumber > bNumber) comparison = 1;
            if (aNumber < bNumber) comparison = -1;

            if (comparison === 0) {
                return new Date(b.rawData) - new Date(a.rawData);
            }

            return comparison;
        });
    }

    return docs;
};

const simpleSort = (docs) => {
    return sortBy(docs, ["type"]);
};

const getAssociatedProducts = (doc) => {
    const isDownloadDocument = has(doc, "documentsAttributes");
    const documentToProducts = isDownloadDocument
        ? checkIsArray(doc.documentsAttributes.documentToProduct)
        : checkIsArray(doc.details.articleToProducts);
    const cadModelsToProduct = isDownloadDocument
        ? checkIsArray(doc.documentsAttributes.cadModelsToProduct)
        : [];

    return [...documentToProducts, ...cadModelsToProduct];
};

const getDate = (doc) => {
    return (
        doc?.details?.lastUpdatedDate ||
        doc?.latestVersionDate ||
        doc?.modified ||
        doc?.date
    );
};

const formatGraphQLDocument = (doc) => {
    const allAssociatedProducts = getAssociatedProducts(doc);

    const productCategories = !isEmpty(allAssociatedProducts)
        ? reduce(
              allAssociatedProducts,
              (categories, product) => {
                  return unionBy(
                      categories,
                      product.productCategories.nodes,
                      "id",
                  );
              },
              [],
          )
        : [];

    const productSkus = !isEmpty(allAssociatedProducts)
        ? reduce(
              allAssociatedProducts,
              (skus, product) => {
                  return skus.concat(product.globalAttributes.sku);
              },
              [],
          )
        : [];

    const documentCategories = doc.documentCategories.nodes;
    const productLines = doc.productLines.nodes;
    const date = getDate(doc);
    const lastUpdated = formatDocumentVersionDate(date);
    const type = documentCategories.map(({ name }) => name).join(", ") || "";
    const formats = doc.formats ? doc.formats.nodes : [];
    const articleTopics = doc.articleTopics ? doc.articleTopics.nodes : [];
    const documentsAttributes = doc?.documentsAttributes;

    const postMimeType = doc.postMimeType ? doc.postMimeType : "";
    const iconFromMimeType = getIconByMimeTypeName(postMimeType);
    const iconClassOverride = doc?.details?.iconClassOverride;

    const iconName = getIconName(
        documentsAttributes,
        iconFromMimeType,
        formats,
        iconClassOverride,
    );

    return {
        id: doc.id,
        documentCategories,
        documentsAttributes,
        productCategories,
        productLines,
        productSkus,
        formats,
        articleTopics,
        iconName,
        lastUpdated,
        lastUpdatedDate: doc.latestVersionDate,
        rawData: date,
        postType: doc.postType,
        postTitle: doc.title,
        title: doc.title,
        url: doc.url,
        type: sanitizeAndParseHTML(type),
        version: doc.latestVersion ? `Version: ${doc.latestVersion}` : "",
        name: createDocumentNameHtml(doc, iconName),
    };
};

const getDocFromGraphql = (elasticsearchDocument, graphqlDocs) => {
    const predicate = {
        databaseId: elasticsearchDocument?.post_id,
    };

    return find(graphqlDocs, predicate);
};

const useDocumentTableBuilder = () => {
    const DOWNLOAD_POST_TYPE = "dlm_download";

    const { allWpDlmdownload } = useAllDownloadData();
    const getBasePath = useGetBasePath(true);

    const buildDocumentForSearch = useCallback(
        (elasticsearchDocument) => {
            const graphqlDocs = allWpDlmdownload?.nodes;

            const graphqlDoc = getDocFromGraphql(
                elasticsearchDocument,
                graphqlDocs,
            );

            if (!graphqlDoc) {
                return null;
            }

            return formatGraphQLDocument(graphqlDoc);
        },
        [allWpDlmdownload?.nodes],
    );

    const buildDocumentLibraryTable = useCallback(
        (hits) => {
            return reduce(
                hits,
                (acc, elasticsearchNode) => {
                    const elasticsearchDocument = elasticsearchNode._source;
                    const postType = elasticsearchDocument?.post_type;
                    const graphqlDocs = allWpDlmdownload?.nodes;

                    if (postType === "knowledge-center") {
                        const doc = getKcAsDoc(
                            elasticsearchDocument,
                            getBasePath,
                        );
                        return [...acc, doc];
                    }

                    const graphqlDoc = getDocFromGraphql(
                        elasticsearchDocument,
                        graphqlDocs,
                    );

                    if (graphqlDoc) {
                        const doc = formatGraphQLDocument(graphqlDoc);
                        return [...acc, doc];
                    }

                    return acc;
                },
                [],
            );
        },
        [allWpDlmdownload?.nodes, getBasePath],
    );

    const buildDocumentTable = useCallback((docs, sortOrder) => {
        const graphqlDocs = map(docs, (doc) => {
            const formattedDoc =
                doc.__typename === "WpArticle"
                    ? {
                          ...doc,
                          slug: getKCUrlFromGraphQLDoc(doc),
                      }
                    : doc;
            return formatGraphQLDocument(formattedDoc);
        });

        if (sortOrder) {
            return sortDocuments(graphqlDocs, sortOrder);
        }

        return simpleSort(graphqlDocs);
    }, []);

    return {
        buildDocumentForSearch,
        buildDocumentLibraryTable,
        buildDocumentTable,
        DOWNLOAD_POST_TYPE,
    };
};

export default useDocumentTableBuilder;
